import configs from '@/config/config.json';

export interface VenueCoordinates {
  latitude: number;
  longitude: number;
  geom: {
    type: 'Point';
    coordinates: [number, number];
    crs: { type: 'name'; properties: { name: 'EPSG:4326' } };
  };
}

interface GeocodeResult {
  partial_match?: boolean;
  types: string[];
  geometry: {
    location_type: string;
    location: { lat: () => number; lng: () => number };
  };
}
interface MapsApi {
  Geocoder: new () => {
    geocode: (
      request: { address: string },
      callback: (results: GeocodeResult[] | null, status: string) => void
    ) => void;
  };
}

const getMaps = () =>
  (window as unknown as { google?: { maps?: MapsApi } }).google?.maps;
let loading: Promise<MapsApi> | undefined;
const timeoutMs = 15000;

function loadMaps(): Promise<MapsApi> {
  const maps = getMaps();
  if (maps?.Geocoder) return Promise.resolve(maps);
  if (loading) return loading;
  loading = new Promise<MapsApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    const script = existing || document.createElement('script');
    const cleanup = () => {
      clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
    const onError = () => {
      cleanup();
      if (!existing) script.remove();
      reject(
        new Error(
          'Address lookup could not load. Check your connection and retry.'
        )
      );
    };
    const onLoad = () => {
      const loaded = getMaps();
      if (!loaded?.Geocoder) {
        onError();
        return;
      }
      cleanup();
      resolve(loaded);
    };
    const timer = setTimeout(onError, timeoutMs);
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (!existing) {
      const environment =
        process.env.REACT_APP_RUNTIME_ENV ||
        process.env.NODE_ENV ||
        'development';
      const config = configs[environment] || configs.development;
      // Include places so the venue editor can reuse this Maps script.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(config.google.api_key)}&libraries=places`;
      script.async = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    loading = undefined;
    throw error;
  });
  return loading;
}

export async function geocodeVenue(
  venue: Record<string, string>
): Promise<VenueCoordinates> {
  const maps = await loadMaps();
  const address = [
    venue.addr_line_1,
    venue.addr_line_2,
    venue.addr_city,
    venue.addr_state,
    venue.addr_zip,
    venue.addr_country,
  ]
    .filter(Boolean)
    .join(', ');
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Address lookup timed out. Retry this location.')),
      timeoutMs
    );
    try {
      new maps.Geocoder().geocode({ address }, (results, status) => {
        clearTimeout(timer);
        if (status !== 'OK' || !results?.length) {
          reject(
            new Error(
              status === 'ZERO_RESULTS'
                ? 'Address not found. Correct the address and upload the CSV again.'
                : `Address lookup failed (${status}). Check Google Geocoding access or retry.`
            )
          );
          return;
        }
        const result = results[0];
        if (
          results.length !== 1 ||
          result.partial_match ||
          result.geometry.location_type === 'APPROXIMATE' ||
          !result.types.some((type) =>
            [
              'street_address',
              'premise',
              'subpremise',
              'establishment',
              'point_of_interest',
            ].includes(type)
          )
        ) {
          reject(
            new Error(
              'Address did not resolve to a clear business location. Correct the address and upload the CSV again.'
            )
          );
          return;
        }
        const latitude = result.geometry.location.lat();
        const longitude = result.geometry.location.lng();
        if (
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude) ||
          Math.abs(latitude) > 90 ||
          Math.abs(longitude) > 180
        ) {
          reject(new Error('Address lookup returned invalid coordinates.'));
          return;
        }
        resolve({
          latitude,
          longitude,
          geom: {
            type: 'Point',
            coordinates: [longitude, latitude],
            crs: { type: 'name', properties: { name: 'EPSG:4326' } },
          },
        });
      });
    } catch {
      clearTimeout(timer);
      reject(
        new Error(
          'Address lookup failed. Check Google Geocoding access or retry.'
        )
      );
    }
  });
}
